/**
 * @file: MentionPickerPanel.tsx
 *
 * Body of the `@mention` picker — rendered inside the shared
 * {@link BottomPanel}. Lists the room's active members (the viewer is
 * already excluded by the caller) plus a leading "所有人" (`@all`) row,
 * with a search field to filter large rooms. Tapping a row reports the
 * chosen target; the screen inserts the `@<name>` chip into the composer.
 */

import { Typography } from '@euler/app/components/typography';
import {
  MENTION_ALL_LABEL,
  MentionCandidate,
} from '@euler/app/flows/chat/utils/mentions';
import { Avatar } from '@euler/components/Avatar';
import { FontFamily } from '@euler/components/typography';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { AtSign, Search } from 'lucide-react-native';
import { FC, useMemo, useState } from 'react';
import { ScrollView, TextInput, TouchableOpacity, View } from 'react-native';

export const MentionPickerPanel: FC<{
  candidates: MentionCandidate[];
  /** Offer the leading "所有人" (@all) row (group rooms). */
  includeAll: boolean;
  onSelect: (candidate: MentionCandidate) => void;
}> = ({ candidates, includeAll, onSelect }) => {
  const { rpx } = useResponsiveContext();
  const [query, setQuery] = useState('');

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return candidates;
    return candidates.filter(c => c.displayName.toLowerCase().includes(q));
  }, [candidates, query]);

  const showAll = includeAll && MENTION_ALL_LABEL.includes(query.trim());

  return (
    <View style={{ flex: 1, paddingTop: rpx(20) }}>
      <View style={{ paddingHorizontal: rpx(20) }}>
        <Typography.Text
          size={rpx(16)}
          color="#1E293B"
          style={{
            fontFamily: FontFamily.NotoSans.Medium,
            marginBottom: rpx(14),
          }}
        >
          选择提醒的人
        </Typography.Text>
        <View
          style={{
            flexDirection: 'row',
            alignItems: 'center',
            backgroundColor: '#F1F5F9',
            borderRadius: rpx(10),
            paddingHorizontal: rpx(12),
            marginBottom: rpx(8),
          }}
        >
          <Search color="#94A3B8" size={rpx(16)} />
          <TextInput
            value={query}
            onChangeText={setQuery}
            placeholder="搜索成员"
            placeholderTextColor="#94A3B8"
            style={{
              flex: 1,
              marginLeft: rpx(8),
              paddingVertical: rpx(10),
              fontSize: rpx(14),
              fontFamily: FontFamily.NotoSans.Regular,
              color: '#1E293B',
            }}
          />
        </View>
      </View>

      <ScrollView
        keyboardShouldPersistTaps="handled"
        style={{ flex: 1 }}
        contentContainerStyle={{
          paddingHorizontal: rpx(20),
          paddingBottom: rpx(20),
        }}
      >
        {showAll ? (
          <TouchableOpacity
            activeOpacity={0.6}
            onPress={() =>
              onSelect({
                target: { kind: 'all' },
                displayName: MENTION_ALL_LABEL,
              })
            }
            style={{
              flexDirection: 'row',
              alignItems: 'center',
              paddingVertical: rpx(10),
            }}
          >
            <View
              style={{
                width: rpx(38),
                height: rpx(38),
                borderRadius: rpx(19),
                backgroundColor: '#EFF6FF',
                alignItems: 'center',
                justifyContent: 'center',
                marginRight: rpx(12),
              }}
            >
              <AtSign color="#3b82f6" size={rpx(20)} />
            </View>
            <Typography.Text
              size={rpx(15)}
              color="#1E293B"
              style={{ fontFamily: FontFamily.NotoSans.Medium }}
            >
              所有人
            </Typography.Text>
          </TouchableOpacity>
        ) : null}

        {filtered.length === 0 && !showAll ? (
          <Typography.Text
            size={rpx(13)}
            color="#94A3B8"
            style={{ paddingVertical: rpx(20), textAlign: 'center' }}
          >
            没有匹配的成员
          </Typography.Text>
        ) : (
          filtered.map(c => (
            <TouchableOpacity
              key={
                c.target.kind === 'user'
                  ? String(c.target.platformUserId)
                  : c.displayName
              }
              activeOpacity={0.6}
              onPress={() => onSelect(c)}
              style={{
                flexDirection: 'row',
                alignItems: 'center',
                paddingVertical: rpx(8),
              }}
            >
              <Avatar
                uri={c.avatarUrl ?? undefined}
                name={c.displayName}
                style={{
                  width: rpx(38),
                  height: rpx(38),
                  borderRadius: rpx(19),
                  marginRight: rpx(12),
                }}
              />
              <Typography.Text
                size={rpx(14)}
                color="#1E293B"
                style={{ flex: 1 }}
              >
                {c.displayName}
              </Typography.Text>
            </TouchableOpacity>
          ))
        )}
      </ScrollView>
    </View>
  );
};
